import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@monorepo/shared-auth/firebaseConfig';
import { IconPhoto, IconUpload, IconTrash, IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { addGalleryPhoto, getGalleryPhotos, deleteGalleryPhoto } from '../../services/leagueService';
import { DateTime } from 'luxon';

const GalleryTab = ({ leagueId, currentUser, isAdmin, isMember }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPhotos();
  }, [leagueId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const photosData = await getGalleryPhotos(leagueId);
      setPhotos(photosData);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not an image file`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name} is too large (max 10MB)`);
          continue;
        }

        const timestamp = Date.now();
        const filename = `leagues/${leagueId}/gallery/${timestamp}_${file.name}`;
        const storageRef = ref(storage, filename);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        await addGalleryPhoto(leagueId, {
          url: downloadURL,
          storagePath: filename,
          fileName: file.name,
          uploadedBy: currentUser.uid,
          uploaderName: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Unknown',
          caption: ''
        });
      }

      await loadPhotos();
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload some photos. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      if (photo.storagePath) {
        const storageRef = ref(storage, photo.storagePath);
        await deleteObject(storageRef);
      }

      await deleteGalleryPhoto(leagueId, photo.id);
      await loadPhotos();
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
  };

  const navigatePhoto = (direction) => {
    const newIndex = selectedIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setSelectedIndex(newIndex);
      setSelectedPhoto(photos[newIndex]);
    }
  };

  const canUpload = isMember || isAdmin;
  const canDelete = (photo) => isAdmin || photo.uploadedBy === currentUser?.uid;

  if (loading) {
    return (
      <div className="ls-gallery-loading">
        <div className="ls-loading-spinner"></div>
        <p>Loading gallery...</p>
      </div>
    );
  }

  return (
    <section className="ls-gallery-tab">
      <header className="ls-gallery-header">
        <div className="ls-gallery-title">
          <IconPhoto size={24} />
          <h3>League Gallery</h3>
          <span className="ls-photo-count">{photos.length} photos</span>
        </div>

        {canUpload && (
          <div className="ls-gallery-upload">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
            <button
              className="ls-btn ls-btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <IconUpload size={18} />
              {uploading ? 'Uploading...' : 'Upload Photos'}
            </button>
          </div>
        )}
      </header>

      {photos.length === 0 ? (
        <div className="ls-gallery-empty">
          <IconPhoto size={48} stroke={1} />
          <h4>No photos yet</h4>
          <p>
            {canUpload
              ? 'Be the first to share a photo from your league!'
              : 'Photos from league events will appear here.'}
          </p>
        </div>
      ) : (
        <div className="ls-gallery-grid">
          {photos.map((photo, index) => (
            <article
              key={photo.id}
              className="ls-gallery-item"
              onClick={() => openLightbox(photo, index)}
            >
              <img src={photo.url} alt={photo.caption || 'League photo'} />
              <div className="ls-gallery-item-overlay">
                <span className="ls-gallery-uploader">{photo.uploaderName}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      {selectedPhoto && (
        <div className="ls-lightbox" onClick={closeLightbox}>
          <div className="ls-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="ls-lightbox-close" onClick={closeLightbox}>
              <IconX size={24} />
            </button>

            {selectedIndex > 0 && (
              <button
                className="ls-lightbox-nav ls-lightbox-prev"
                onClick={() => navigatePhoto(-1)}
              >
                <IconChevronLeft size={32} />
              </button>
            )}

            <img src={selectedPhoto.url} alt={selectedPhoto.caption || 'League photo'} />

            {selectedIndex < photos.length - 1 && (
              <button
                className="ls-lightbox-nav ls-lightbox-next"
                onClick={() => navigatePhoto(1)}
              >
                <IconChevronRight size={32} />
              </button>
            )}

            <div className="ls-lightbox-info">
              <p className="ls-lightbox-uploader">
                Uploaded by {selectedPhoto.uploaderName}
              </p>
              {selectedPhoto.uploadedAt && (
                <p className="ls-lightbox-date">
                  {DateTime.fromMillis(selectedPhoto.uploadedAt.toMillis()).toFormat('MMM d, yyyy')}
                </p>
              )}
              {canDelete(selectedPhoto) && (
                <button
                  className="ls-btn ls-btn-danger ls-btn-small"
                  onClick={() => handleDeletePhoto(selectedPhoto)}
                >
                  <IconTrash size={16} />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GalleryTab;
